import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useId } from 'react';

import { ModularLayoutBasicData } from '../../../data/model';
import { ModularLayoutsTranslation } from '../../../localization/SiteTranslation';
import Button from '../../../shared/components/Button/Button';
import TertiaryButton from '../../../shared/components/Button/TertiaryButton';
import Icon from '../../../shared/components/Icon/Icon';
import Image from '../../../shared/components/Image/Image';
import List from '../../../shared/components/List/List';
import { Focusable, Tooltip, TooltipTrigger } from '../../../shared/components/Tooltip/Tooltip';
import Heading from '../../../shared/components/Typography/Heading/Heading';
import ExternalConfiguratorModalState from '../ExternalConfiguratorModal/ExternalConfiguratorModalState';
import css from './ModularLayouts.css';

interface ModularLayoutsProps {
  layouts: ModularLayoutBasicData[];
  configuratorModal: ExternalConfiguratorModalState;
  translation: ModularLayoutsTranslation;
  className?: string;
}

const ModularLayouts = ({ className, configuratorModal, layouts, translation }: ModularLayoutsProps) => {
  const headingId = useId();

  return (
    <section className={classNames(css.root, className)} aria-labelledby={headingId}>
      <div className={css.inner}>
        <header className={css.header}>
          <Heading id={headingId} level={2} className={css.title}>
            {translation.title}
          </Heading>
          <p className={css.description}>{translation.description}</p>
          {configuratorModal.canConfigure() && (
            <p className={css.headerAction}>
              <span className={css.text}>{translation.configureFromScratchText}</span>
              <TooltipTrigger>
                <Focusable>
                  <button type="button" className={css.button} onClick={() => configuratorModal.open()}>
                    {translation.configureFromScratchButton}
                  </button>
                </Focusable>
                <Tooltip offset={16} placement="top">
                  {configuratorModal.translation.triggerTooltip}
                </Tooltip>
              </TooltipTrigger>
            </p>
          )}
        </header>
        <List
          className={css.list}
          itemClassName={css.item}
          items={layouts}
          onItemClick={(item: ModularLayoutBasicData) =>
            configuratorModal.canConfigureLayout(item) ? configuratorModal.open(item) : undefined
          }
          itemRenderer={(item: ModularLayoutBasicData) => (
            <ModularLayoutsItem
              data={item}
              canConfigure={configuratorModal.canConfigureLayout(item)}
              translation={translation}
            />
          )}
          keyProperty="id"
        />
        {configuratorModal.canConfigure() && (
          <div className={css.footerAction}>
            <span className={css.text}>{translation.configureFromScratchText}</span>
            <TertiaryButton type="button" className={css.footerButton} onClick={() => configuratorModal.open()}>
              {translation.configureFromScratchMobileButton}
            </TertiaryButton>
          </div>
        )}
      </div>
    </section>
  );
};

export default observer(ModularLayouts);

interface ModularLayoutsItemProps {
  data: ModularLayoutBasicData;
  translation: ModularLayoutsTranslation;
  canConfigure: boolean;
}

const ModularLayoutsItem = ({ data, canConfigure, translation }: ModularLayoutsItemProps) => {
  return (
    <>
      <div className={css.container}>
        <Image
          className={css.imageContainer}
          classNameImageElement={css.image}
          src={data.thumbnail.url}
          alt=""
          width={390}
          height={300}
          scalingMode="fit"
          sharpeningFactor={2}
          size="contain"
          loading="lazy"
        />
        {canConfigure && (
          <Button type="button" className={css.button}>
            {translation.configureLayoutButton}
            <span className="visuallyHidden"> {data.name}</span>
            <Icon name="edit" className={css.hoverIcon} />
            <Icon name="angleRight" className={css.touchIcon} />
          </Button>
        )}
      </div>
      <p className={css.name}>{data.name}</p>
    </>
  );
};
